import React, { useId } from "react";
import PropTypes from "prop-types";
import {
  VARIABLES_HINT,
  MIN_OFFSET_DAYS,
  previewMessage,
} from "../followUpConstants";
import styles from "./StepItem.module.css";

/**
 * Editor de uma única mensagem da cadência.
 *
 * Campos:
 * - "Enviar no dia X" (offsetDays) com label contextual conforme a posição.
 * - Textarea da mensagem (messageTemplate) + hint fixo de variáveis.
 * - Preview inline com variáveis substituídas por exemplos.
 *
 * Reordenação via botões ↑↓ (sem drag-and-drop). Remoção via ✕ (o pai garante
 * o mínimo de 1 step desabilitando o botão).
 */
export default function StepItem({
  index,
  total,
  offsetDays,
  messageTemplate,
  error = null,
  onChangeOffset,
  onChangeMessage,
  onMoveUp,
  onMoveDown,
  onRemove,
}) {
  const offsetId = useId();
  const messageId = useId();
  const isFirst = index === 0;
  const isLast = index === total - 1;

  const offsetLabel = isFirst
    ? "Enviar quantos dias depois de entrar na cadência"
    : "Enviar quantos dias depois da mensagem anterior";

  const preview = previewMessage(messageTemplate);

  return (
    <li className={styles.step}>
      <div className={styles.stepTop}>
        <span className={styles.stepNumber} aria-hidden="true">
          {index + 1}
        </span>
        <span className={styles.stepHeading}>Mensagem {index + 1}</span>

        <div className={styles.stepControls}>
          <button
            type="button"
            className={styles.iconBtn}
            onClick={onMoveUp}
            disabled={isFirst}
            aria-label={`Mover mensagem ${index + 1} para cima`}
          >
            ↑
          </button>
          <button
            type="button"
            className={styles.iconBtn}
            onClick={onMoveDown}
            disabled={isLast}
            aria-label={`Mover mensagem ${index + 1} para baixo`}
          >
            ↓
          </button>
          <button
            type="button"
            className={styles.removeBtn}
            onClick={onRemove}
            disabled={total <= 1}
            aria-label={`Remover mensagem ${index + 1}`}
          >
            ✕
          </button>
        </div>
      </div>

      <div className={styles.field}>
        <label htmlFor={offsetId} className={styles.label}>
          {offsetLabel}
        </label>
        <input
          id={offsetId}
          type="number"
          min={MIN_OFFSET_DAYS}
          max={365}
          className={styles.offsetInput}
          value={offsetDays}
          onChange={(e) => onChangeOffset(e.target.value)}
          aria-label={offsetLabel}
        />
      </div>

      <div className={styles.field}>
        <label htmlFor={messageId} className={styles.label}>
          Texto da mensagem
        </label>
        <textarea
          id={messageId}
          className={styles.textarea}
          rows={3}
          value={messageTemplate}
          onChange={(e) => onChangeMessage(e.target.value)}
          placeholder="Oi {{clientName}}, sentimos sua falta na {{tenantName}}! Que tal agendar seu próximo {{serviceName}}?"
          aria-invalid={Boolean(error)}
          aria-describedby={`${messageId}-hint`}
        />
        <p id={`${messageId}-hint`} className={styles.hint}>
          {VARIABLES_HINT}
        </p>
        {error && (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}

        {messageTemplate.trim() && (
          <div className={styles.preview}>
            <span className={styles.previewTag} aria-hidden="true">
              Prévia
            </span>
            <p className={styles.previewText}>{preview}</p>
          </div>
        )}
      </div>
    </li>
  );
}

StepItem.propTypes = {
  index: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  offsetDays: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  messageTemplate: PropTypes.string.isRequired,
  error: PropTypes.string,
  onChangeOffset: PropTypes.func.isRequired,
  onChangeMessage: PropTypes.func.isRequired,
  onMoveUp: PropTypes.func.isRequired,
  onMoveDown: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};
